import { DIALOG_DATA } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, Inject, OnInit, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  EReplaceOrderReason,
  ReasonKey,
  ReplaceOrderDialogData,
  returnReasonMap,
  ApiResponse,
  ApiErrorResponse,
  isErrorResponse,
} from '@shabic/models';
import { DialogComponent } from '../dialog/dialog.component';
import { OrderMediaService } from '@shabic/core';
import { switchMap, catchError, of } from 'rxjs';

@Component({
  selector: 'shabic-replace-order-dialog',
  templateUrl: './replace-order-dialog.component.html',
  styleUrls: ['./replace-order-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReplaceOrderDialogComponent implements OnInit {
  readonly EReplaceOrderReason = EReplaceOrderReason;
  
  formGroup = new FormGroup({
    reason: new FormControl(null, [Validators.required]),
    description: new FormControl(''),
  });

  selectedFiles: File[] = [];
  filePreviews: Map<File, string> = new Map();
  isDragOver = false;
  uploadErrors: string[] = [];
  isUploading = false;
  
           // File validation constants
         readonly MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
         readonly MAX_FILES = 3;
         readonly ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  ngOnInit() {
    // Add conditional validation for description field
    this.formGroup.get('reason')?.valueChanges.subscribe(reason => {
      const descriptionControl = this.formGroup.get('description');
      if (reason === null) {
        descriptionControl?.setValidators([Validators.required]);
        // Clear the reason validation error when "Other" is selected
        this.formGroup.get('reason')?.setErrors(null);
      } else {
        descriptionControl?.clearValidators();
        descriptionControl?.setValue(''); // Clear the description when other reason is selected
      }
      descriptionControl?.updateValueAndValidity();
    });
  }

  // Form controls for each product's replacement quantity
  productForms: FormGroup[] = [];

  constructor(
    @Inject(DIALOG_DATA) public data: ReplaceOrderDialogData,
    private translateService: TranslateService,
    private orderMediaService: OrderMediaService
  ) {
    this.initializeProductForms();
  }

  private initializeProductForms(): void {
    this.productForms = this.data.order.products.map(product => {
      return new FormGroup({
        productId: new FormControl(product.product.id),
        quotationId: new FormControl(product.quotationId),
        quantity: new FormControl(product.quantity, [
          Validators.required,
          Validators.min(1),
          Validators.max(product.quantity)
        ]),
        originalQuantity: new FormControl(product.quantity),
        productName: new FormControl(product.product.name.get('en'))
      });
    });
  }

  get reasonControl() {
    return this.formGroup.get('reason') as FormControl;
  }

  get hasValidReplacements(): boolean {
    return this.productForms.some(form => {
      const quantity = form.get('quantity')?.value;
      return quantity && quantity > 0;
    });
  }

  isFormValid(): boolean {
    const selectedReason = this.formGroup.value.reason;
    const descriptionText = this.formGroup.value.description;
    
    // If no reason is selected at all
    if (selectedReason === null && !descriptionText) {
      return false;
    }
    
    // If "Other" is selected, description is required
    if (selectedReason === null && (!descriptionText || descriptionText.trim() === '')) {
      return false;
    }
    
    return true;
  }

  confirm(dialog: DialogComponent) {
    // Check if form is valid considering the conditional validation
    const selectedReason = this.formGroup.value.reason;
    const descriptionText = this.formGroup.value.description;
    
    // If "Other" is selected, description is required
    if (selectedReason === null && (!descriptionText || descriptionText.trim() === '')) {
      this.formGroup.get('description')?.markAsTouched();
      return;
    }
    
    // If no reason is selected at all
    if (selectedReason === null && !descriptionText) {
      this.formGroup.get('reason')?.markAsTouched();
      return;
    }
    
    if (!this.hasValidReplacements) {
      return;
    }

    const reason = selectedReason as unknown as ReasonKey;
    const description = returnReasonMap.get(reason) || descriptionText || '';

    const replacements = this.productForms
      .filter(form => form.get('quantity')?.value > 0)
      .map(form => ({
        productId: form.get('productId')?.value,
        quotationId: form.get('quotationId')?.value,
        quantity: form.get('quantity')?.value,
      }));

    // Step 1: Upload images if any are selected
    if (this.selectedFiles.length > 0) {
      this.isUploading = true;
      console.log('Starting image upload for', this.selectedFiles.length, 'files');
      this.orderMediaService
        .uploadOrderImages(this.selectedFiles)
        .pipe(
          switchMap((uploadResponse: ApiResponse<number[] | ApiErrorResponse>) => {
            console.log('Upload response received:', uploadResponse);
            if (isErrorResponse(uploadResponse)) {
              console.error('Image upload failed:', uploadResponse);
              this.isUploading = false;
              return of(null);
            }
            
            // Media IDs are already extracted by the service
            const mediaIds = uploadResponse.payload;
            console.log('Media IDs from service:', mediaIds);
            
            // Ensure we have valid media IDs (not an error response)
            if (Array.isArray(mediaIds)) {
              // Step 2: Call the replace endpoint with media IDs
              return this.data
                .onConfirm({
                  description: this.translateService.instant(description),
                  replacements,
                  photos: mediaIds, // Pass media IDs instead of files
                });
            } else {
              console.error('Invalid media IDs received:', mediaIds);
              this.isUploading = false;
              return of(null);
            }
          }),
          catchError(error => {
            console.error('Error in replace order process:', error);
            this.isUploading = false;
            return of(null);
          })
        )
        .subscribe((result) => {
          console.log('Final result:', result);
          this.isUploading = false;
          if (result !== null) {
            dialog.close();
          }
        });
    } else {
      console.log('No files to upload, proceeding directly with replace');
      // No images to upload, proceed directly with replace
      this.data
        .onConfirm({
          description: this.translateService.instant(description),
          replacements,
          photos: [], // No photos
        })
        .subscribe(() => {
          dialog.close();
        });
    }
  }

  getReasonKey(reason: ReasonKey) {
    return returnReasonMap.get(reason) || '';
  }

  getMaxQuantity(form: FormGroup): number {
    return form.get('originalQuantity')?.value || 0;
  }

  getCurrentQuantity(form: FormGroup): number {
    return form.get('quantity')?.value || 0;
  }

  // Enhanced file handling methods
  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    this.processFiles(files);
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files) {
      this.processFiles(files);
    }
  }

  private processFiles(files: FileList) {
    this.uploadErrors = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file
      const validationResult = this.validateFile(file);
      if (validationResult.isValid) {
        // Check if we already have too many files
        if (this.selectedFiles.length >= this.MAX_FILES) {
          this.uploadErrors.push(`Maximum ${this.MAX_FILES} files allowed`);
          break;
        }
        
        this.selectedFiles.push(file);
        this.createFilePreview(file);
      } else {
        if (validationResult.error) {
          this.uploadErrors.push(validationResult.error);
        }
      }
    }
  }

  private validateFile(file: File): { isValid: boolean; error?: string } {
    // Check file type
    if (!this.ALLOWED_TYPES.includes(file.type)) {
      return { 
        isValid: false, 
        error: `Invalid file type: ${file.type}. Only JPEG, PNG, and WebP images are allowed.` 
      };
    }
    
    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      return { 
        isValid: false, 
        error: `File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size is ${this.MAX_FILE_SIZE / 1024 / 1024}MB.` 
      };
    }
    
    return { isValid: true };
  }

  async createFilePreview(file: File) {
    try {
      // Compress image if it's too large
      const compressedFile = await this.compressImage(file);
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.filePreviews.set(compressedFile, e.target.result);
        // Update the file reference to use compressed version
        const index = this.selectedFiles.indexOf(file);
        if (index > -1) {
          this.selectedFiles[index] = compressedFile;
        }
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Error creating preview:', error);
      // Fallback to original file if compression fails
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.filePreviews.set(file, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }

  private async compressImage(file: File): Promise<File> {
    // Only compress if file is larger than 1MB
    if (file.size <= 1024 * 1024) {
      return file;
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (maintain aspect ratio)
        const maxWidth = 1920;
        const maxHeight = 1080;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          0.8 // Quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  getFilePreview(file: File): string {
    return this.filePreviews.get(file) || '';
  }

  removeFile(index: number) {
    const file = this.selectedFiles[index];
    this.selectedFiles.splice(index, 1);
    this.filePreviews.delete(file);
  }

  clearAllFiles() {
    this.selectedFiles = [];
    this.filePreviews.clear();
    this.uploadErrors = [];
  }

  getFileSize(file: File): string {
    const sizeInMB = file.size / 1024 / 1024;
    if (sizeInMB < 1) {
      return `${(file.size / 1024).toFixed(1)}KB`;
    }
    return `${sizeInMB.toFixed(1)}MB`;
  }

  getTotalFileSize(): string {
    const totalSize = this.selectedFiles.reduce((total, file) => total + file.size, 0);
    const sizeInMB = totalSize / 1024 / 1024;
    if (sizeInMB < 1) {
      return `${(totalSize / 1024).toFixed(1)}KB`;
    }
    return `${sizeInMB.toFixed(1)}MB`;
  }
} 